/**
 * HostTermPicker lets the owner choose what the room calls a host: a common
 * name such as "Teacher" or one they type. Shows the room's refusal when the
 * change is not saved.
 */
import * as React from 'react'
import { ActionButton } from '../../library/ActionButton/ActionButton'
import { roomMenuTestIds } from '../RoomMenu/roomMenu.testIds'

export const HOST_TERM_CHOICES = ['Host', 'Teacher', 'Facilitator']

export interface HostTermPickerProps {
  hostTerm: string
  /** Rejects with the room's reason when the change is refused. */
  onSave: (hostTerm: string) => Promise<void>
  onDone: () => void
}

export function HostTermPicker({ hostTerm, onSave, onDone }: HostTermPickerProps) {
  const isChoice = HOST_TERM_CHOICES.includes(hostTerm)
  const [choice, setChoice] = React.useState(isChoice ? hostTerm : 'other')
  const [otherTerm, setOtherTerm] = React.useState(isChoice ? '' : hostTerm)
  const [isSaving, setIsSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const term = (choice === 'other' ? otherTerm : choice).trim()

  const save = async () => {
    setIsSaving(true)
    setError(null)
    try {
      await onSave(term)
      onDone()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rename hosts')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <fieldset className="flex flex-col gap-2 rounded border border-basic p-3" data-testid={roomMenuTestIds.hostTermPicker}>
      <legend className="px-1 text-xs font-semibold text-primary">Call hosts</legend>
      {HOST_TERM_CHOICES.map((option) => (
        <label key={option} className="flex cursor-pointer items-center gap-2 text-sm text-primary">
          <input type="radio" name="host-term" checked={choice === option} onChange={() => setChoice(option)} />
          {option}
        </label>
      ))}
      <label className="flex cursor-pointer items-center gap-2 text-sm text-primary">
        <input type="radio" name="host-term" checked={choice === 'other'} onChange={() => setChoice('other')} />
        <input
          type="text"
          aria-label="Other name for hosts"
          placeholder="Other"
          maxLength={24}
          value={otherTerm}
          onFocus={() => setChoice('other')}
          onChange={(event) => setOtherTerm(event.target.value)}
          className="min-w-0 flex-1 rounded border border-basic bg-surface px-2 py-1 text-sm text-primary"
        />
      </label>
      {error && <p className="text-xs text-danger" role="alert">{error}</p>}
      <div className="flex justify-end gap-2">
        <ActionButton variant="outline" onClick={onDone} disabled={isSaving}>Cancel</ActionButton>
        <ActionButton variant="default" accentBackground onClick={() => void save()} disabled={isSaving || !term}>Save</ActionButton>
      </div>
    </fieldset>
  )
}
